/**
 * Shared module layout used by Sprints 7-11.
 * Composed strictly from frozen primitives (Card + NavLink + Outlet).
 * No new visual language; renders the same eyebrow + tab-row pattern the
 * frozen sprints (Marketing / Commerce / Operations / Finance) already use.
 */
import { NavLink, Outlet } from "react-router-dom";

export type ModuleTab = { to: string; label: string; testId?: string };

export function ModuleLayout({
  eyebrow, title, tabs, subtitle, testId,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  tabs: ModuleTab[];
  testId?: string;
}) {
  return (
    <div data-testid={testId}>
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">{eyebrow}</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-slate-500">{subtitle}</div>}
      </div>
      <nav
        className="mb-6 flex flex-wrap gap-1 pb-3"
        style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end
            data-testid={t.testId ?? `nav-${t.to}`}
            className={({ isActive }) =>
              "px-3 py-1.5 text-sm font-medium transition-colors"
            }
            style={({ isActive }) => ({
              color: isActive ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              background: isActive ? "var(--ph-neutral-tint)" : "transparent",
              borderRadius: "var(--ph-radius-sm)",
            })}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
